import { useFieldArray, useFormContext } from "react-hook-form";
import React from "react";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Box } from "../../Box";
import { Flex } from "../../Flex";
import { FieldErrorMessage } from "../FieldErrorMessage";
import { Button } from "../../Button";
import { Fieldset } from "../Fieldset/Fieldset";
import { FormArrayRow } from "./FormArrayRow";
/**
 * FormArray component for rendering a form array field. It handles adding, removing, and sorting array items.
 * It also enforces minimum and maximum array item constraints.
 *
 * @param name - Unique identifier for the form array field
 * @param label - Optional label text to display above the form array
 * @param children - Child components to render within each array item. The chidren are required otherwise
 *                   the field will not know how to render rows.
 * @param minLength - Minimum number of array items required (default: 1)
 * @param maxLength - Maximum number of array items allowed (default: Infinity)
 * @param defaultValue - Default values for new array items
 * @param enableSorting - Whether to enable drag and drop sorting (default: true)
 * @param multiLine - Whether to display array items as multiline. The label will be display above the control.
 *                    If false the label will be inlined with the control (default: true)
 * @param required - Whether the field is required (default: false)
 * @param disabled - Whether the field is disabled (default: false)
 * @param readOnly - Whether the field is read-only (default: false)
 * @param hasError - Whether the field has validation errors (default: false)
 * @param isSuccess - Whether the field has a success state (default: false)
 * @param placeholder - Placeholder text to display when there are no array items
 *
 * @returns The rendered FormArray component
 */
export const FormArray = ({ name, label, children, minLength = 1, maxLength = Infinity, enableSorting = true, multiLine = true, required = false, disabled = false, readOnly = false, hasError = false, isSuccess = false, placeholder = "No items added", }) => {
    var _a;
    const { formState, getFieldState } = useFormContext();
    const { error } = getFieldState(name, formState);
    const { control, getValues, setValue } = useFormContext();
    const { fields, append, remove, move } = useFieldArray({
        control,
        // Required for the DnD functionality
        keyName: "rowId",
        name,
        rules: {
            maxLength: { message: `Field has too many elements, maximum ${maxLength} elements`, value: maxLength },
            minLength: { message: `Field is missing elements, minimum ${minLength} elements`, value: minLength },
            required: { message: "Field is required", value: required },
        },
    });
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
    }));
    const handleAdd = () => {
        if (fields.length < maxLength) {
            append({});
        }
    };
    const handleRemove = (index) => {
        if (fields.length > minLength) {
            remove(index);
        }
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = fields.findIndex((field) => field.rowId === active.id);
            const newIndex = fields.findIndex((field) => field.rowId === over.id);
            // Get the current values
            const values = getValues(name);
            // Create a new array with the reordered values
            const newValues = [...values];
            const [movedItem] = newValues.splice(oldIndex, 1);
            newValues.splice(newIndex, 0, movedItem);
            // First move the fields to update the internal state
            move(oldIndex, newIndex);
            // Then update the form values with the reordered values
            setValue(name, newValues, {
                shouldDirty: true,
                shouldTouch: true,
            });
        }
    };
    // Add useEffect to ensure minimum rows. When the component is first rendered, it will add the
    // minimum number of rows.
    React.useEffect(() => {
        const rowsToAdd = minLength - fields.length;
        if (rowsToAdd > 0) {
            // Add missing rows to meet minimum requirement
            Array.from({ length: rowsToAdd }).forEach(() => {
                append({});
            });
        }
    }, [minLength, fields.length, append]);
    const emptyState = (React.createElement(Flex, { sx: {
            alignItems: "center",
            flexDirection: "column",
            gap: 2,
            justifyContent: "center",
            py: 4,
        } },
        React.createElement(Box, { sx: { fontSize: 1 } }, placeholder),
        React.createElement(Button, { variant: "secondary", onClick: handleAdd, disabled: disabled || readOnly, sx: { alignItems: "center", display: "flex", gap: 1 } }, "Add Item")));
    const content = (React.createElement(Flex, { sx: { flexDirection: "column", gap: 1, width: "100%" } }, fields.length === 0
        ? emptyState
        : fields.map((field, index) => (React.createElement(FormArrayRow, { key: field.rowId, id: field.rowId, index: index, name: name, minLength: minLength, maxLength: maxLength, fieldsLength: fields.length, onAdd: handleAdd, onRemove: handleRemove, enableSorting: enableSorting && fields.length > 1, multiLine: multiLine, disabled: disabled, readOnly: readOnly }, children)))));
    return (React.createElement(Box, { sx: { my: 2 } },
        React.createElement(Fieldset, { legend: label, readOnly: readOnly, disabled: disabled, hasError: hasError || Boolean(error === null || error === void 0 ? void 0 : error.root), required: required, isSuccess: isSuccess }, enableSorting ? (React.createElement(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd },
            React.createElement(SortableContext, { items: fields.map((field) => field.rowId), strategy: verticalListSortingStrategy }, content))) : (content)),
        React.createElement(FieldErrorMessage, { name: name, error: (_a = error === null || error === void 0 ? void 0 : error.root) === null || _a === void 0 ? void 0 : _a.message })));
};
